import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: "website" | "article";
  ogImage?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = "website",
  ogImage,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
}: SEOProps) {
  useEffect(() => {
    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    document.title = title;
    setMeta("description", description);
    setMeta("keywords", keywords);

    const link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (link) {
      link.href = canonicalUrl;
    } else {
      const newLink = document.createElement("link");
      newLink.rel = "canonic";
      newLink.href = canonicalUrl;
      document.head.appendChild(newLink);
    }

    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", canonicalUrl, true);
    if (ogImage) {
      setMeta("og:image", ogImage, true);
    }

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    if (ogImage) {
      setMeta("twitter:image", ogImage);
    }

    const schema = document.querySelector('script[type="application/ld+json"]');
    if (schema) schema.remove();

    const baseSchema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description,
      url: canonicalUrl,
    };

    const breadcrumbEl = document.getElementById("breadcrumb-schema");
    if (breadcrumbEl) {
      const breadcrumbData = JSON.parse(breadcrumbEl.textContent || "");
      const webPageSchema = document.createElement("script");
      webPageSchema.type = "application/ld+json";
      webPageSchema.textContent = JSON.stringify({
        ...baseSchema,
        ...(breadcrumbData.itemListElement ? { breadcrumb: breadcrumbData } : {}),
      });
      document.head.appendChild(webPageSchema);
    } else {
      const schemaScript = document.createElement("script");
      schemaScript.type = "application/ld+json";
      schemaScript.textContent = JSON.stringify(baseSchema);
      document.head.appendChild(schemaScript);
    }

    if (ogType === "article") {
      if (articlePublishedTime) {
        setMeta("article:published_time", articlePublishedTime, true);
      }
      if (articleModifiedTime) {
        setMeta("article:modified_time", articleModifiedTime, true);
      }
      if (articleAuthor) {
        setMeta("article:author", articleAuthor, true);
      }
    }
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, articlePublishedTime, articleModifiedTime, articleAuthor]);

  return null;
}